import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Check, Loader2 } from "lucide-react";
import Seo from "@/components/Seo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ORG_TYPES = [
  "Senior communities",
  "Housing authorities",
  "Activity directors",
  "Assisted living facilities",
  "Community centers",
  "Senior centers and program staff",
];

const Organizations = () => {
  const [form, setForm] = useState({ name: "", organization: "", email: "", phone: "", estimated_quantity: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, {
        kind: "organization",
        name: form.name,
        organization: form.organization || null,
        email: form.email,
        phone: form.phone || null,
        estimated_quantity: form.estimated_quantity ? Number(form.estimated_quantity) : null,
        message: form.message,
      });
      setSent(true);
      toast.success("Request received — we will be in touch with a custom quote.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const inputCls = "bg-white border-2 border-border rounded-xl px-4 py-4 h-auto text-lg focus:ring-2 focus:ring-sage focus:border-transparent outline-none transition-all";

  return (
    <div data-testid="organizations-page" className="bg-cream">
      <Seo
        title="Organization & Bulk Orders — UNSTUCK Seniors for Senior Communities"
        description="Special pricing for senior communities, housing authorities, activity directors, assisted living facilities, and community centers ordering 10 or more UNSTUCK Seniors programs. Activity director resources with inclusive senior activities."
        path="/organizations"
      />
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-16 sm:py-24">
        <div className="grid gap-14 lg:grid-cols-2 lg:items-start">
          <div>
            <p className="text-sm uppercase tracking-widest font-semibold text-gold-dark mb-4">
              Organizations &amp; bulk orders
            </p>
            <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tight leading-tight text-ink mb-6">
              Bring UNSTUCK Seniors to your community.
            </h1>
            <p className="text-lg sm:text-xl leading-relaxed text-ink/80 mb-8">
              UNSTUCK Seniors works one-on-one, partner-to-partner, or in small groups — and it fits
              naturally into activity programming, resident engagement, and family visits.
            </p>
            <ul className="grid sm:grid-cols-2 gap-3 mb-10">
              {ORG_TYPES.map((item) => (
                <li key={item} className="flex items-start gap-2 text-base text-ink/85">
                  <Check className="h-5 w-5 text-sage shrink-0 mt-0.5" aria-hidden="true" />
                  {item}
                </li>
              ))}
            </ul>
            <div className="bg-white border-l-4 border-gold rounded-r-2xl p-6">
              <p className="text-lg leading-relaxed text-ink/85">
                Special organization pricing is available for orders of{" "}
                <span className="font-semibold text-ink">10 or more programs</span>. Contact us for a
                custom quote.
              </p>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-border shadow-[0_4px_20px_rgba(0,0,0,0.05)] p-8 sm:p-10">
            {sent ? (
              <div data-testid="org-form-success" className="text-center py-10">
                <div className="h-16 w-16 rounded-full bg-sage-light flex items-center justify-center mx-auto mb-6">
                  <Check className="h-8 w-8 text-sage" aria-hidden="true" />
                </div>
                <h2 className="font-heading font-bold text-2xl text-ink mb-3">Request received</h2>
                <p className="text-lg text-ink/75">
                  Thank you — we have your details and will follow up with a custom quote.
                </p>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="org-pricing-form" className="space-y-5">
                <h2 className="font-heading font-bold text-2xl text-ink mb-2">Request Organization Pricing</h2>
                <div>
                  <Label htmlFor="org-name" className="text-base font-medium mb-1.5 block">Your name *</Label>
                  <Input id="org-name" data-testid="org-name-input" required value={form.name} onChange={update("name")} className={inputCls} />
                </div>
                <div>
                  <Label htmlFor="org-org" className="text-base font-medium mb-1.5 block">Organization</Label>
                  <Input id="org-org" data-testid="org-organization-input" value={form.organization} onChange={update("organization")} className={inputCls} />
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <Label htmlFor="org-email" className="text-base font-medium mb-1.5 block">Email *</Label>
                    <Input id="org-email" data-testid="org-email-input" type="email" required value={form.email} onChange={update("email")} className={inputCls} />
                  </div>
                  <div>
                    <Label htmlFor="org-phone" className="text-base font-medium mb-1.5 block">Phone</Label>
                    <Input id="org-phone" data-testid="org-phone-input" type="tel" value={form.phone} onChange={update("phone")} className={inputCls} />
                  </div>
                </div>
                <div>
                  <Label htmlFor="org-qty" className="text-base font-medium mb-1.5 block">Estimated number of programs (10+)</Label>
                  <Input id="org-qty" data-testid="org-quantity-input" type="number" min="1" value={form.estimated_quantity} onChange={update("estimated_quantity")} className={inputCls} />
                </div>
                <div>
                  <Label htmlFor="org-message" className="text-base font-medium mb-1.5 block">Tell us about your program *</Label>
                  <Textarea id="org-message" data-testid="org-message-input" required rows={4} value={form.message} onChange={update("message")} className={inputCls} />
                </div>
                <Button
                  type="submit"
                  disabled={sending}
                  data-testid="org-submit-button"
                  className="w-full bg-sage text-white hover:bg-sage-dark rounded-full px-8 py-4 h-auto text-lg font-medium transition-all duration-300"
                >
                  {sending && <Loader2 className="h-5 w-5 animate-spin mr-2" aria-hidden="true" />}
                  Request Organization Pricing
                </Button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Organizations;
